/**
 * The control a platform operator uses to enter a school, and the banner that
 * says they are in one.
 *
 * Rendered for nobody else. It is keyed off a capability the account actually
 * has — `tenants:view`, which only `system_admin` holds — rather than off the
 * role name, so a second platform role later gets the control by being granted
 * the module rather than by being added to a list here.
 *
 * The banner is the more important half. An operator working inside a
 * customer's records with no indication of it is one careless edit away from
 * changing the wrong school's data, and the same screen serves every tenant,
 * so the only thing distinguishing Riverview from the next customer is this
 * strip. It is deliberately loud and deliberately not dismissible.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { api, ApiError, type PlatformTenant } from '@/lib/api'
import { useSession } from '@/lib/session'
import { IconChevronDown } from './icons'

export function TenantSwitcher() {
  const { session, can, refresh } = useSession()
  const [open, setOpen] = useState(false)
  const [tenants, setTenants] = useState<PlatformTenant[] | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [problem, setProblem] = useState<string | null>(null)
  const ref = useRef<HTMLDivElement>(null)

  // Mid-view-as the permissions on screen are the viewed person's, which
  // would hide this control and leave an operator inside a school with no
  // visible way out; being inside another tenant at all means operator.
  const isOperator = can('tenants', 'view') || session?.homeTenantId != null

  useEffect(() => {
    if (!open || tenants) return
    let cancelled = false
    void api
      .platformTenants()
      .then((result) => {
        if (!cancelled) setTenants(result.items)
      })
      .catch((caught) => {
        if (!cancelled) {
          setProblem(caught instanceof ApiError ? caught.message : 'The tenant list could not be loaded.')
        }
      })
    return () => {
      cancelled = true
    }
  }, [open, tenants])

  useEffect(() => {
    if (!open) return
    function onPointerDown(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false)
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  const switchTo = useCallback(
    async (tenantId: string | null) => {
      setBusy(tenantId ?? 'home')
      setProblem(null)
      try {
        await api.switchTenant(tenantId)
        // The switch takes effect on the *next* request, so everything already
        // on screen now describes the tenant just left. A full reload is the
        // honest response: refetching piecemeal would leave one tenant's
        // student list beside another's dashboard, and no component expects
        // the tenant under it to change.
        window.location.assign('/')
      } catch (caught) {
        setProblem(caught instanceof ApiError ? caught.message : 'The tenant could not be changed.')
        setBusy(null)
        await refresh()
      }
    },
    [refresh],
  )

  if (!session || !isOperator) return null

  const working = session.homeTenantId != null

  return (
    <div className="tenant-switch" ref={ref}>
      <button
        className="tenant-switch__trigger"
        type="button"
        onClick={() => setOpen((shown) => !shown)}
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <span className="tenant-switch__label">
          <span className="tenant-switch__eyebrow">{working ? 'Working in' : 'Platform'}</span>
          <span className="tenant-switch__name">{session.tenant.name}</span>
        </span>
        <IconChevronDown className="tenant-switch__caret" />
      </button>

      {open && (
        <div className="tenant-switch__menu" role="menu">
          {working && (
            <button
              className="tenant-switch__leave"
              type="button"
              role="menuitem"
              disabled={busy !== null}
              onClick={() => void switchTo(null)}
            >
              {busy === 'home' ? 'Leaving…' : `Leave ${session.tenant.name}`}
            </button>
          )}
          {problem && (
            <p className="tenant-switch__problem" role="alert">
              {problem}
            </p>
          )}

          {tenants === null && !problem && <p className="tenant-switch__loading">Loading tenants…</p>}

          {tenants?.map((tenant) => (
            <button
              key={tenant.id}
              className="tenant-switch__option"
              type="button"
              role="menuitem"
              disabled={tenant.isActive || busy !== null}
              onClick={() => void switchTo(tenant.isHome ? null : tenant.id)}
            >
              <span className="tenant-switch__option-name">
                {tenant.name}
                {tenant.isHome && <span className="tenant-switch__tag">Your organisation</span>}
              </span>
              <span className="tenant-switch__option-meta">
                {tenant.isActive
                  ? 'Current'
                  : busy === tenant.id
                    ? 'Entering…'
                    : `${tenant.edition === 'pro' ? 'Pro' : 'Lite'} · ${tenant.subscriptionStatus}`}
              </span>
            </button>
          ))}

          <p className="tenant-switch__footnote">
            Entering a school is recorded in that school&rsquo;s audit log, and everything you do there is
            recorded against your own account.
          </p>
        </div>
      )}
    </div>
  )
}
